import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X, Search } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { SncfVoyageursLogo, FluoLogo } from './SncfVoyageursLogo';
import StationPicker from './StationPicker';
import LanguageSwitcher from './LanguageSwitcher';

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  const nav = [
    { to: '/', label: t('nav.home') },
    { to: '/#commute', label: t('nav.commute') },
    { to: '/#info-fluo', label: t('nav.info_fluo') },
    { to: '/tableau', label: t('nav.board') }
  ];

  const isActive = (to) => {
    const [path, hash] = to.split('#');
    if (hash) return location.pathname === path && location.hash === `#${hash}`;
    return location.pathname === to && !location.hash;
  };

  /** SPA-friendly anchor navigation: when on the same page, smooth-scroll;
   *  otherwise navigate first, the home page useEffect will handle scrolling. */
  const handleNavClick = (e, to) => {
    if (!to.includes('#')) return;
    const [path, hash] = to.split('#');
    if (location.pathname === path) {
      e.preventDefault();
      const el = document.getElementById(hash);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // Update hash without reloading
        window.history.replaceState(null, '', `#${hash}`);
      }
    } else {
      e.preventDefault();
      navigate(`${path}#${hash}`);
    }
  };

  return (
    <header className="sticky top-0 z-40 bg-white border-b border-slate-200">
      <div className="h-1.5 diag-stripes" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16 gap-4">
          <Link to="/" className="flex items-center gap-2 sm:gap-3 group min-w-0">
            <SncfVoyageursLogo height={28} className="shrink-0" />
            <div className="h-7 w-px bg-slate-200" />
            <FluoLogo height={24} className="shrink-0 sm:h-7" />
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {nav.map(item => {
              const active = isActive(item.to);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={(e) => handleNavClick(e, item.to)}
                  data-testid={`nav-${item.to.replace(/[/#]/g, '') || 'home'}`}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    active
                      ? 'bg-ter-blue text-white'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-2">
            <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
              <DialogTrigger asChild>
                <Button variant="ghost" size="icon" aria-label={t('search_dialog.title')}>
                  <Search className="w-4 h-4" />
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-2xl">
                <DialogHeader>
                  <DialogTitle className="text-ter-blue">{t('search_dialog.title')}</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-slate-600">{t('search_dialog.text')}</p>
                <div className="pt-2" onClick={() => setTimeout(() => setSearchOpen(false), 200)}>
                  <StationPicker />
                </div>
              </DialogContent>
            </Dialog>

            <button
              onClick={() => document.dispatchEvent(new CustomEvent('toggleA11y', { detail: { position: 'top' } }))}
              className="flex items-center justify-center gap-1 px-2 h-10 rounded-md bg-ter-blue text-white font-bold text-sm hover:bg-opacity-80 transition-colors"
              title={t('accessibility.title')}
              aria-label={t('accessibility.title')}
            >
              <span>A+</span>
              <span className="text-xl leading-none">♿</span>
            </button>

            <LanguageSwitcher />

            <Button variant="outline" size="icon" className="lg:hidden" onClick={() => setOpen(!open)} aria-label="Menu">
              {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </div>
      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white">
          <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col gap-1">
            {nav.map(item => (
              <Link
                key={item.to}
                to={item.to}
                onClick={(e) => { handleNavClick(e, item.to); setOpen(false); }}
                className={`px-3 py-2 rounded-md text-sm font-medium ${
                  isActive(item.to) ? 'bg-ter-blue text-white' : 'text-slate-700 hover:bg-slate-100'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
